/**
 * Jungle FX — "Frequently bought together" side panel for the Enterprise cart drawer.
 *
 * Opens beside the drawer on screens 1024px and wider whenever the drawer opens with items in it.
 * Suggestions are for the most recently added product (the first cart line), using Shopify's
 * complementary products (Search & Discovery app) and falling back to related products.
 * Adding from the panel mirrors Enterprise's product-form.js: it posts to cart/add.js with the
 * cart drawer and cart icon sections, re-renders the drawer and fires the theme's cart events.
 */
if (!customElements.get('ja-cart-fbt')) {
  class JaCartFbt extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        // Moved in the page: resume watching the drawer without adding listeners twice.
        if (this.observer) this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
        window.addEventListener('resize', this.resizeHandler, { passive: true });
        document.addEventListener('keydown', this.tabHandler, true);
        return;
      }
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) return;
      this.initialised = true;

      this.list = this.querySelector('.ja-fbt__list');
      this.headingText = this.querySelector('.ja-fbt__heading-text');
      this.productName = this.querySelector('.ja-fbt__product');
      this.limit = parseInt(this.dataset.limit || '4', 10);
      this.mq = window.matchMedia('(min-width: 1024px)');
      this.cache = new Map();
      this.dismissedFor = null;

      this.querySelector('.ja-fbt__close').addEventListener('click', () => this.dismiss());
      this.addEventListener('keyup', (evt) => {
        if (evt.key === 'Escape') document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
      });
      this.addEventListener('submit', this.handleSubmit.bind(this));
      this.addEventListener('change', JaCartFbt.handleVariantChange);

      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });

      this.resizeHandler = () => this.position();
      window.addEventListener('resize', this.resizeHandler, { passive: true });

      // Keyboard: the drawer keeps Tab inside itself, so while the panel is open, Tab from the
      // drawer's last stop moves into the panel and the panel's last stop leads back to the drawer.
      this.tabHandler = this.handleTab.bind(this);
      document.addEventListener('keydown', this.tabHandler, true);
      this.mq.addEventListener('change', () => this.sync());

      document.addEventListener('ja:cart:updated', () => {
        if (!this.drawer.hasAttribute('open')) return;
        clearTimeout(this.refreshTimer);
        // Let the "Added" state show before the list re-filters.
        this.refreshTimer = setTimeout(() => this.load(), 1200);
      });

      this.sync();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.resizeHandler);
      document.removeEventListener('keydown', this.tabHandler, true);
    }

    static focusables(root) {
      return Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');
    }

    handleTab(evt) {
      if (evt.key !== 'Tab' || !this.classList.contains('is-open') || !this.drawer.hasAttribute('open')) return;
      const inDrawer = JaCartFbt.focusables(this.drawer);
      const inPanel = JaCartFbt.focusables(this);
      if (!inDrawer.length || !inPanel.length) return;
      const active = document.activeElement;
      let next = null;
      if (!evt.shiftKey && active === inDrawer[inDrawer.length - 1]) next = inPanel[0];
      else if (!evt.shiftKey && active === inPanel[inPanel.length - 1]) next = inDrawer[0];
      else if (evt.shiftKey && active === inPanel[0]) next = inDrawer[inDrawer.length - 1];
      else if (evt.shiftKey && active === inDrawer[0]) next = inPanel[inPanel.length - 1];
      if (!next) return;
      evt.preventDefault();
      evt.stopPropagation();
      next.focus();
    }

    /** Opens or closes the panel to match the drawer. */
    sync() {
      if (this.drawer.hasAttribute('open') && this.mq.matches) {
        this.load();
      } else {
        if (!this.drawer.hasAttribute('open')) {
          this.anchor = null;
          // Each time the drawer opens, start with its own list hidden again.
          document.body.classList.remove('ja-fbt-empty');
        }
        this.hide();
      }
    }

    position() {
      const width = this.drawer.getBoundingClientRect().width;
      if (width) this.style.setProperty('--ja-drawer-w', `${Math.round(width)}px`);
    }

    async getCart() {
      if (window.jaCart) return window.jaCart.get(true);
      const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return res.ok ? res.json() : null;
    }

    async fetchSuggestions(productId, intent) {
      const key = `${productId}:${intent}`;
      if (this.cache.has(key)) return this.cache.get(key);
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', this.limit + 6);
      url.searchParams.set('section_id', 'ja-cart-upsell');
      url.searchParams.set('intent', intent);
      const promise = fetch(url.toString())
        .then((res) => (res.ok ? res.text() : ''))
        .then((html) => {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          return Array.from(tmpl.content.querySelectorAll('.ja-fbt-item'));
        })
        .catch(() => []);
      this.cache.set(key, promise);
      return promise;
    }

    /** Loads suggestions for the anchor cart line and shows the panel if there are any. */
    async load() {
      const cart = await this.getCart();
      if (!cart || !cart.items || cart.items.length === 0 || !this.drawer.hasAttribute('open')) {
        this.hide();
        return;
      }

      // The anchor stays the same while the drawer is open, so adding a suggestion doesn't swap
      // the whole list over to that product. A new drawer opening starts from the newest line.
      let anchor = this.anchor && cart.items.find((line) => line.product_id === this.anchor.product_id);
      if (!anchor) anchor = cart.items[0];
      this.anchor = anchor;

      if (this.dismissedFor === anchor.product_id) {
        document.body.classList.add('ja-fbt-empty');
        return;
      }
      this.dismissedFor = null;

      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      let items = await this.fetchSuggestions(anchor.product_id, 'complementary');
      let intent = 'complementary';
      items = items.filter((el) => !inCart.has(el.dataset.productId));
      if (items.length === 0) {
        intent = 'related';
        items = (await this.fetchSuggestions(anchor.product_id, 'related'))
          .filter((el) => !inCart.has(el.dataset.productId));
      }

      if (!this.drawer.hasAttribute('open') || !this.mq.matches) return;

      if (items.length === 0) {
        // Nothing to suggest here: let the drawer's own "You might also like" list show instead.
        document.body.classList.add('ja-fbt-empty');
        this.hide();
        return;
      }

      this.headingText.textContent = intent === 'complementary'
        ? this.dataset.heading
        : this.dataset.fallbackHeading;
      this.productName.textContent = intent === 'complementary' ? anchor.product_title : '';
      this.productName.hidden = intent !== 'complementary';

      this.list.replaceChildren(...items.slice(0, this.limit).map((el) => {
        const node = el.cloneNode(true);
        node.setAttribute('role', 'listitem');
        return node;
      }));

      this.show();
    }

    show() {
      this.position();
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-ready');
      document.body.classList.add('ja-fbt-on');
      document.body.classList.remove('ja-fbt-empty');
      requestAnimationFrame(() => {
        // Wait for the drawer to finish sliding in before the panel follows.
        clearTimeout(this.showTimer);
        this.showTimer = setTimeout(() => this.classList.add('is-open'), 260);
      });
    }

    hide() {
      clearTimeout(this.showTimer);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ja-fbt-on');
    }

    /** Closes the panel until a different product is added. */
    dismiss() {
      if (this.anchor) this.dismissedFor = this.anchor.product_id;
      document.body.classList.add('ja-fbt-empty');
      this.hide();
    }

    static handleVariantChange(evt) {
      const select = evt.target.closest('.ja-fbt-item__select');
      if (!select) return;
      const option = select.options[select.selectedIndex];
      const item = select.closest('.ja-fbt-item');
      const now = item.querySelector('[data-ja-fbt-price]');
      const was = item.querySelector('[data-ja-fbt-was]');
      if (now && option.dataset.price) now.textContent = option.dataset.price;
      if (was) {
        was.textContent = option.dataset.was || '';
        was.hidden = !option.dataset.was;
      }
      if (now) now.classList.toggle('is-sale', Boolean(option.dataset.was));
    }

    async handleSubmit(evt) {
      const form = evt.target.closest('.ja-fbt-item__form');
      if (!form) return;
      evt.preventDefault();

      const btn = form.querySelector('[name="add"]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');

      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData(form);
      let sections = 'cart-icon-bubble';
      if (cartDrawer) sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      try {
        const variantId = Number(formData.get('id'));
        const oldCart = window.jaCart && window.jaCart.data;
        const oldLine = oldCart && oldCart.items ? oldCart.items.find((line) => line.variant_id === variantId) : null;

        const res = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || res.status);

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections && data.sections['cart-icon-bubble']) {
          bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        btn.classList.add('is-success');
        const item = form.closest('.ja-fbt-item');
        if (item) item.classList.add('is-added');

        if (cartDrawer && data.sections) cartDrawer.renderContents(data, false);

        document.dispatchEvent(new CustomEvent(oldLine ? 'on:line-item:change' : 'on:cart:add', {
          bubbles: true,
          detail: oldLine
            ? { variantId: data.variant_id, oldQuantity: oldLine.quantity, newQuantity: data.quantity }
            : { variantId: data.variant_id }
        }));
      } catch (error) {
        btn.classList.remove('is-loading');
        const item = form.closest('.ja-fbt-item');
        if (item) {
          let msg = item.querySelector('.ja-fbt-item__error');
          if (!msg) {
            msg = document.createElement('p');
            msg.className = 'ja-fbt-item__error';
            msg.setAttribute('role', 'alert');
            form.insertAdjacentElement('afterend', msg);
          }
          msg.textContent = (error && error.message) || (window.theme && theme.strings.cartError) || '';
        }
        document.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error && error.message } }));
      } finally {
        btn.removeAttribute('aria-disabled');
        setTimeout(() => btn.classList.remove('is-loading', 'is-success'), 1400);
      }
    }
  }

  customElements.define('ja-cart-fbt', JaCartFbt);
}
